/**
 * Edition Selector - Shared component for changing card editions
 *
 * Wraps EditionBrowser with confirmation modal and pre-fetching logic.
 * Used by collection (changing owned card's edition) and lists (selecting display edition).
 */
import type { ReactElement } from 'react';
import { useState } from 'react';

import { useAtomValue } from 'jotai';

import { collectionOwnedByScryfallIdAtom } from '@/features/collection/state/collectionAtom';
import { useCardLookup } from '@/hooks/useCardLookup';
import type { MinimalCard } from '@/services/cardDatabase';

import CardImage from '../CardImage/CardImage';
import Modal from '../Modal/Modal';
import EditionBrowser from './EditionBrowser';

export interface EditionSelectorProps {
  /** Card name to browse editions for */
  cardName: string;
  /** Currently selected scryfall_id (to highlight) */
  currentScryfallId: string;
  /** Current set code (for "from X to Y" display) */
  currentSet?: string;
  /** Current collector number (for "from X to Y" display) */
  currentCollectorNumber?: string;
  /** Callback when edition is confirmed */
  onEditionChange: (scryfallId: string) => void | Promise<void>;
  /** Whether a change operation is in progress */
  isUpdating?: boolean;
  /** Section label in the panel */
  label?: string;
  /** Modal title */
  modalTitle?: string;
  /** Description text shown in modal */
  description?: string;
  /** Whether to show card image preview in modal */
  showCardPreview?: boolean;
}

export default function EditionSelector({
  cardName,
  currentScryfallId,
  currentSet,
  currentCollectorNumber,
  onEditionChange,
  isUpdating = false,
  label = 'Change Edition',
  modalTitle = 'Change Edition',
  description,
  showCardPreview = true,
}: EditionSelectorProps): ReactElement {
  const ownedQuantities = useAtomValue(collectionOwnedByScryfallIdAtom);
  const { hasFullCard, prefetchCard } = useCardLookup();
  const [pendingEdition, setPendingEdition] = useState<MinimalCard | null>(null);
  const [isFetchingCard, setIsFetchingCard] = useState(false);

  const handleEditionSelect = (edition: MinimalCard) => {
    // Don't show confirmation if selecting the same edition
    if (edition.id === currentScryfallId) {
      return;
    }
    setPendingEdition(edition);
  };

  const handleConfirmChange = async () => {
    if (!pendingEdition) return;

    try {
      // Pre-fetch the new card data if not in cache to avoid loading state issues
      if (!hasFullCard(pendingEdition.id)) {
        setIsFetchingCard(true);
        await prefetchCard(pendingEdition.id);
        setIsFetchingCard(false);
      }

      await onEditionChange(pendingEdition.id);
      setPendingEdition(null);
    } catch (error) {
      console.error('Failed to change edition:', error);
      setIsFetchingCard(false);
    }
  };

  const isLoading = isUpdating || isFetchingCard;

  // Build confirmation text
  const showFromTo = currentSet && currentCollectorNumber;
  const confirmationText = showFromTo ? (
    <>
      Change this card from{' '}
      <span className="text-pearl font-medium">
        {currentSet.toUpperCase()} #{currentCollectorNumber}
      </span>{' '}
      to{' '}
      <span className="text-pearl font-medium">
        {pendingEdition?.set.toUpperCase()} #{pendingEdition?.collector_number}
      </span>
      ?
    </>
  ) : (
    <>
      Change to{' '}
      <span className="text-pearl font-medium">
        {pendingEdition?.set.toUpperCase()} #{pendingEdition?.collector_number}
      </span>
      ?
    </>
  );

  return (
    <>
      <EditionBrowser
        cardName={cardName}
        selectedId={currentScryfallId}
        ownedQuantities={ownedQuantities}
        onSelect={handleEditionSelect}
        selectable={!isLoading}
        label={label}
      />

      {/* Confirmation Modal */}
      <Modal isOpen={!!pendingEdition} onClose={() => setPendingEdition(null)} title={modalTitle} size="sm">
        <div className="space-y-4">
          {/* Card preview */}
          {showCardPreview && pendingEdition && (
            <div className="flex justify-center">
              <CardImage
                imageUris={pendingEdition.image_uris}
                name={pendingEdition.name}
                size="normal"
                className="h-64 w-44 rounded-lg shadow-lg"
              />
            </div>
          )}
          <p className={`text-silver text-sm ${showCardPreview ? 'text-center' : ''}`}>{confirmationText}</p>
          {description && <p className={`text-ash text-xs ${showCardPreview ? 'text-center' : ''}`}>{description}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => setPendingEdition(null)}
              className="text-silver hover:text-pearl rounded-lg px-4 py-2 text-sm transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmChange}
              className="bg-primary hover:bg-primary/80 text-pearl rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? 'Changing...' : 'Change Edition'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
